import React, { useCallback, useEffect, useRef } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  IconButton,
  Modal,
  Paper,
  Stack,
  Typography,
} from '@mui/material'

interface ICaptchaModalProps {
  open: boolean
  onToken: (token: string) => void
  onClose: () => void
  onError?: (message: string) => void
}

const API_BASE = process.env.REACT_APP_API_BASE_URL || ''
const CAPTCHA_URL = `${API_BASE}/smarttech/v1/get-captcha`

const CaptchaModal: React.FC<ICaptchaModalProps> = ({
  open,
  onToken,
  onClose,
  onError,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const handledRef = useRef(false)

  const extractTokenFromIframe = useCallback(() => {
    if (handledRef.current) return
    try {
      const href = iframeRef.current?.contentWindow?.location?.href
      if (!href) return
      const url = new URL(href)
      if (!url.pathname.includes('/get-captcha/success')) return

      const token = url.searchParams.get('captchaToken')
      if (token) {
        handledRef.current = true
        onToken(token)
      }
    } catch (error) {
      // Cross-origin read blocked by the browser. Expected when the iframe
      // origin differs from the web app. Fallback paths (postMessage from
      // backend success page) will handle it in that case.
      console.log('here', error)
    }
  }, [onToken])

  useEffect(() => {
    if (!open) {
      handledRef.current = false
      return
    }

    const expectedOrigin = (() => {
      try {
        return new URL(API_BASE).origin
      } catch {
        return ''
      }
    })()

    const handleMessage = (event: MessageEvent) => {
      if (expectedOrigin && event.origin !== expectedOrigin) return

      const data = event.data as { captchaToken?: string, error?: string } | null
      if (!data || typeof data !== 'object') return

      if (data.captchaToken && !handledRef.current) {
        handledRef.current = true
        onToken(data.captchaToken)
        return
      }

      if (data.error) {
        onError?.('CAPTCHA verification failed. Please try again.')
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [open, onToken, onError])

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="captcha-modal-title">
      <Stack
        justifyContent="center"
        alignItems="center"
        sx={{ minHeight: '100dvh' }}
      >
        <Paper sx={{ width: 'min(92vw, 520px)', p: 6, position: 'relative' }}>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            id="captcha-modal-title"
            variant="h6"
            sx={{ mb: 4, pr: 6 }}
          >
            Please complete the CAPTCHA to continue scanning.
          </Typography>
          <Box
            sx={{
              width: '100%',
              height: 480,
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <iframe
              ref={iframeRef}
              src={CAPTCHA_URL}
              title="CAPTCHA challenge"
              style={{ width: '100%', height: '100%', border: 'none' }}
              onLoad={extractTokenFromIframe}
            />
          </Box>
        </Paper>
      </Stack>
    </Modal>
  )
}

export default CaptchaModal
